import {useContext, useEffect, useState} from "react";
import {Box, Container, SelectChangeEvent, Typography,} from "@mui/material";
import Grid from '@mui/material/Grid2';

import {handleInputChangeSelect,} from "../../../utils/formUtil";
import {CONTEXT_ACTION_TYPE, DataStoreContext, DataStoreType} from "../contexts/WorkContext";
import {WorkSortOrderValue} from "../../../constants/constants";
import SearchForm from "../components/forms/SearchForm";
import SortOrder, {useSortOrder} from "../components/forms/SortOrder";
import {FormWorkSearchCond, workAttrList} from "../types/workForm";
import {findWork, FindWorkResponseData} from "../../../utils/awsAmplifyUtil";
import DetailPageLink from "../components/links/DetailPageLink";
import WorkListItem from "../components/WorkListItem";
import useFetchAllQuery, {FetchMessage, STATUSES, useFetchAllQueryHelper} from "../../../hooks/useFetchAllQuery";
import {excludeClassesWithChildren} from "../../../utils/workUtil";
import {WorkClassDocument} from "../../../types/pj1dbApiWork";
import useRepresentativeModel from "../hooks/useRepresentativeModel";

export default function WorkList() {
  const {state, dispatch} = useContext(DataStoreContext);
  const [searchCond, setSearchCond] = useState<FormWorkSearchCond>(state.workListPage.searchCond);
  const {sortWorkList} = useSortOrder();
  const convertWorkClasses = (data: DataStoreType['workListPage']['workClasses']) => sortWorkList(
    excludeClassesWithChildren(
      data.map(workClasssResult => Object.values(workClasssResult.items)).flat()
    ),
    searchCond.sortOrder
  );
  const [workClasses, setWorkClasses] = useState<WorkClassDocument[]>(convertWorkClasses(state.workListPage.workClasses));
  const {status, data, fetchAll} = useWorkClasses({searchCond});
  const {searchStatus} = useFetchAllQueryHelper({
    onChange: (newData, newStatus) => {
      const newWorkClasses = convertWorkClasses(newData);
      const newSearchCond = {...searchCond, listCount: newWorkClasses.length};
      setWorkClasses(newWorkClasses);
      setSearchCond(newSearchCond);
      dispatch({
        type: CONTEXT_ACTION_TYPE.WORK_LIST, payload: {
          ...state.workListPage,
          workClasses: newData,
          searchStatus: newStatus,
          searchCond: newSearchCond,
        } satisfies DataStoreType['workListPage']
      });
    },
    data,
    status,
    initialStatus: state.workListPage.searchStatus,
  })

  const changeSortOrder = (event: SelectChangeEvent<WorkSortOrderValue>) => {
    const sortOrder = event.target.value as WorkSortOrderValue;
    handleInputChangeSelect<FormWorkSearchCond>(event, "sortOrder", searchCond, setSearchCond);
    dispatch({
      type: CONTEXT_ACTION_TYPE.WORK_LIST, payload: {
        ...state.workListPage,
        searchCond: {...searchCond, sortOrder},
      } satisfies DataStoreType['workListPage']
    });
    setWorkClasses(sortWorkList(workClasses, sortOrder));
  }

  const {models, isLoading} = useRepresentativeModel({
    workClasses,
    acquiredRepresentativeModels: state.workListPage.workModels,
  });
  useEffect(() => {
    dispatch({
      type: CONTEXT_ACTION_TYPE.WORK_LIST, payload: {
        ...state.workListPage,
        workModels: models,
      } satisfies DataStoreType['workListPage']
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [models]);

  return (
    <>
      <SearchForm
        isLoading={searchStatus === STATUSES.FETCHING}
        searchCond={searchCond}
        setSearchCond={setSearchCond}
        onSubmit={(e) => {
          e.preventDefault();
          fetchAll().catch(console.error);
        }}
      />

      <Container
        sx={{p: 0, display: "flex", flexDirection: "row", flexWrap: "wrap"}}
        maxWidth={false}
      >
        <Grid container alignItems="end" sx={{mt: 2, flexWrap: "nowrap"}} size={{xs: 12}}>
          <Grid size="grow">
            <Typography variant="subtitle1" sx={{fontWeight: "bold"}}>
              検索結果一覧
            </Typography>
          </Grid>
          <Grid size="auto">
            <Grid container alignItems="center" spacing={2} sx={{flexWrap: "nowrap"}}>
              <Grid>
                <Box component="span">
                  {searchCond.listCount > 0 ? `${searchCond.listCount.toLocaleString()}件` : ""}
                </Box>
              </Grid>
              <Grid>
                <Box component="span">
                  <SortOrder value={searchCond.sortOrder} onChange={changeSortOrder}/>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid container alignItems="stretch" spacing={2} sx={{mt: 2}}>
          {workClasses.map(workClass => (
            <Grid key={workClass.attr[workAttrList.classId.key]}>
              <DetailPageLink work={workClass} style={{textDecoration: 'none'}}>
                <WorkListItem work={workClass} representativeModels={state.workListPage.workModels} isLoading={isLoading}/>
              </DetailPageLink>
            </Grid>
          ))}
        </Grid>

        <FetchMessage data={workClasses} status={searchStatus}/>
      </Container>
    </>
  );
}

function useWorkClasses({searchCond}: { searchCond: FormWorkSearchCond }) {
  const queryResult = useFetchAllQuery<FindWorkResponseData<'class'>>({
    queryFn: (query) => {
      const params = {
        version: "work-user",
        queryAttr: {
          ...(searchCond.keyword === "" ? {} : {
            $or: [
              {[workAttrList.nameJp.key]: {"$regex": searchCond.keyword, "$options": "i"}},
              {[workAttrList.nameEn.key]: {"$regex": searchCond.keyword, "$options": "i"}},
              {[workAttrList.code.key]: {"$regex": searchCond.keyword, "$options": "i"}},
              {[workAttrList.manufacturer.key]: {"$regex": searchCond.keyword, "$options": "i"}},
            ]
          }),
        },
      };

      return findWork('class', params, query);
    },
    needToFetchMore: (data, limit) => Object.entries(data?.items ?? {}).length === limit,
  });

  return queryResult;
}
